import { WebData, webDataErrorSimple, webDataLoading, WebDataSteps, webDataSuccess, unwrapOr } from "@lvt/web-data";
import { Box } from "@material-ui/core";
import { Alert, AlertTitle, Skeleton } from "@material-ui/lab";
import { useEffect, useState, useContext } from "react";

import CameraList from "@/common/components/ArchivePage/CameraList/CameraList";
import DateTimeHeader from "@/common/components/ArchivePage/DateTimeHeader/DateTimeHeader";
import InformationalVideoModalWrapper from "@/common/components/InformationalVideoModal/InformationalVideoModalWrapper";
import { Page } from "@/common/components/InformationalVideoModal/types";
import { ArchiveMetadataContext } from "@/common/contexts/ArchiveMetadataContext";
import { LiveUnitMetadataContextWrapper } from "@/common/contexts/LiveUnitMetadataContext";
import { RootState, useDispatch, useSelector } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import {
    setLiveUnitId,
    setCamerasOrCabinetsMeta,
    setUnitMeta,
    setCanRequestVideo,
} from "@/store/archives/slice";
import { useFullStory } from "@/utilities/hooks/fullstory";
import {
    ArchiveImagesNavMeta,
    isArchiveImagesNavMeta,
    isCameraOrCabinetMetadata,
    NullArchiveImagesNavMeta,
} from "@/utilities/types/archive";
import * as Types from "@/utilities/types/archive";

import { dispatchUtilFn } from "./ArchivesPage.Utils";


enum ArchivePageTypeTypes {
    LiveUnit = "LiveUnit",
    Cabinet = "Cabinet",
}
interface ArchivePageType<T> {
    type: T;
}
interface LiveUnitArchivePageType extends ArchivePageType<ArchivePageTypeTypes.LiveUnit> {
    liveUnitId: number;
}

export const liveUnitArchivePage = (liveUnitId: number): PageType => ({
    type: ArchivePageTypeTypes.LiveUnit,
    liveUnitId,
});


interface CabinetArchivePageType extends ArchivePageType<ArchivePageTypeTypes.Cabinet> {
    cabinetId: number;
}

export const cabinetArchivePage = (cabinetId: number): PageType => ({
    type: ArchivePageTypeTypes.Cabinet,
    cabinetId,
});

// TODO: should this be some word that means cabinet or liveunit in a more generic sense?
export type PageType = LiveUnitArchivePageType | CabinetArchivePageType;
export interface ArchivePageParams {
    pageType: PageType;
}

export const ArchivesPage: React.FC<ArchivePageParams> = ({ pageType }) => {
    const archiveMetadataService = useContext(ArchiveMetadataContext);
    const dispatch = useDispatch();
    const FullStory = useFullStory();
    const [navHidden] = useSelector((state) => [state.liveUnitNav.hidden]);
    const unitMeta: WebData<Types.ArchiveImagesNavMeta> = useSelector((state: RootState) => state.archives.unitMeta);
    const [liveUnitMetadata, setLiveUnitMetadata] = useState<ArchiveImagesNavMeta>(
        unwrapOr(unitMeta, NullArchiveImagesNavMeta),
    );

    useEffect(() => {
        switch (pageType.type) {
            case ArchivePageTypeTypes.LiveUnit:
                // The state canRequestVideo is used to update the date time headers and optionally show the Select Video button on the history page.
                // One cannot select historic media from an AC.
                // This is NOT checking for user permissions to be able to view the historic media
                dispatchUtilFn(()=>dispatch(setCanRequestVideo(true)),()=>dispatch(setLiveUnitId(pageType.liveUnitId)));

                const alreadyFetchedUnit =
                    unitMeta.type === WebDataSteps.Success && pageType.liveUnitId === unitMeta.payload.liveUnitId;

                if (!alreadyFetchedUnit) {
                    dispatch(setUnitMeta(webDataLoading()));
                    dispatch(setCamerasOrCabinetsMeta(webDataLoading()));

                    archiveMetadataService.fetchLiveUnitMetadata(pageType.liveUnitId).then((response) => {
                        if (isArchiveImagesNavMeta(response)) {
                            setLiveUnitMetadata(response);
                            dispatch(setUnitMeta(webDataSuccess(response)));

                            archiveMetadataService
                                .fetchCameraOrCabinetMetadata(response.cameraIds, null)
                                .then((response) => {
                                    if (isCameraOrCabinetMetadata(response)) {
                                        dispatch(setCamerasOrCabinetsMeta(webDataSuccess(response)));
                                    } else {
                                        dispatch(
                                            setCamerasOrCabinetsMeta(webDataErrorSimple({ message: response.message })),
                                        );
                                        dispatch(
                                            addAlert({
                                                severity: Severity.Error,
                                                message: `Failed to retrieve camera metadata: ${response.message}`,
                                            }),
                                        );
                                    }
                                });
                        } else {
                            setUnitMeta(webDataErrorSimple({ message: response.message }));
                            dispatch(
                                addAlert({
                                    severity: Severity.Error,
                                    message: `Failed to retrieve live unit metadata: ${response.message}`,
                                }),
                            );
                        }
                    });
                }

                break;
            case ArchivePageTypeTypes.Cabinet:
                dispatchUtilFn(()=>dispatch(setCanRequestVideo(false)), ()=>dispatch(setLiveUnitId(null)));

                const alreadyFetchedCabinet =
                    unitMeta.type === WebDataSteps.Success && pageType.cabinetId === unitMeta.payload.liveUnitId;

                if (!alreadyFetchedCabinet) {
                    dispatch(setUnitMeta(webDataLoading()));
                    dispatch(setCamerasOrCabinetsMeta(webDataLoading()));

                    archiveMetadataService.fetchArchiveCabinetMetadata(pageType.cabinetId).then((response) => {
                        if (isArchiveImagesNavMeta(response)) {
                            setLiveUnitMetadata(response);
                            dispatch(setUnitMeta(webDataSuccess(response)));

                            archiveMetadataService
                                .fetchCameraOrCabinetMetadata(null, [pageType.cabinetId])
                                .then((response) => {
                                    if (isCameraOrCabinetMetadata(response)) {
                                        dispatch(setCamerasOrCabinetsMeta(webDataSuccess(response)));
                                    } else {
                                        dispatch(
                                            setCamerasOrCabinetsMeta(webDataErrorSimple({ message: response.message })),
                                        );
                                        dispatch(
                                            addAlert({
                                                severity: Severity.Error,
                                                message: `Failed to retrieve archive cabinet metadata: ${response.message}`,
                                            }),
                                        );
                                    }
                                });
                        } else {
                            setUnitMeta(webDataErrorSimple({ message: response.message }));
                            dispatch(
                                addAlert({
                                    severity: Severity.Error,
                                    message: `Failed to retrieve archive cabinet metadata: ${response.message}`,
                                }),
                            );
                        }
                    });
                }

                break;
        }

        FullStory.event(
            FullStory.names.events.historyPageLoaded,
            pageType.type === ArchivePageTypeTypes.Cabinet ? { archive_cabinet_id: pageType.cabinetId } : { live_unit_id: pageType.liveUnitId },
        );
    }, [pageType]);

    const waitOnHeader = () => {
        switch (unitMeta.type) {
            case WebDataSteps.NotAsked:
            case WebDataSteps.Loading:
                return <Skeleton variant="rect" width="100%" height={120} />;
            case WebDataSteps.Success:
                return <DateTimeHeader />;
            case WebDataSteps.Error:
                return (
                    <Alert severity="error">
                        <AlertTitle>Error</AlertTitle>
                        This page has failed to load. <strong>Please reload and try again.</strong>
                    </Alert>
                );
        }
    };

    return (
        <>
            <Box
                display="flex"
                flexDirection="column"
                height={`calc(100vh - ${navHidden ? 0 : 85}px`}
            >
                <InformationalVideoModalWrapper page={Page.VmsHistory} />
                <LiveUnitMetadataContextWrapper metadata={liveUnitMetadata}>
                    {waitOnHeader()}
                    <CameraList />
                </LiveUnitMetadataContextWrapper>
            </Box>
        </>
    );
};
